import React, { useMemo } from 'react';
import axios from 'axios';
import numberFormatter from 'utils/formatNumber';
import {
  AUDIENCE_LAL_MODAL_TYPE,
  AudiencePlatformLabel,
  COMBINED_AUDIENCE_GENERATION,
  ENUM_SYSTEM_USED,
  LoadingStates,
  OPERATOR_TYPE,
} from '../../constants/index';
import { AUDIENCE_STUDIO_API_ENDPOINT } from '../../pages/audienceManager/variables';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Slider,
  Typography,
} from '@mui/material';
import { CircularProgress } from '@material-ui/core';
import {
  addNotification,
  updateCombinedAudience,
} from '../../pages/audienceManager/slice';
import { groupModeledAudienceCountByIdType } from '../../helper/helper';
import { makeStyles } from '@material-ui/core/styles';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useUser } from 'reactfire';

const useStyles = makeStyles(theme => ({
  containerBorder: {
    margin: '2rem',
    marginBottom: '6rem',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '0rem',
    marginBottom: '1rem',
  },
  operatorText: {
    minWidth: '40px',
    paddingTop: '6px',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  divider: {
    width: '100%',
    color: '#ccc',
    opacity: '0.2',
    marginTop: '0px',
    marginBottom: '0px',
  },
  slider: {
    width: '96% !important',
    marginLeft: '16px !important',
    maxWidth: '1060px',
    color: '#413762 !important',
    '& > .MuiSlider-markLabel': {
      fontFamily: 'Calibre-Regular,sans-serif',
    },
  },
  headerRow: {
    fontWeight: 600,
    color: '#555',
    marginBottom: 8,
    paddingTop: '16px',
    paddingBottom: '-16px',
  },
  generateHeaderText: {
    fontSize: '22px',
    fontFamily: 'Calibre-Semibold, sans-serif',
    fontWeight: '600',
    lineHeight: '32px',
  },
  generateButton: {
    marginTop: '16px',
  },
  subText: {
    color: '#685d6f',
    fontSize: 12,
    marginRight: 5,
  },
  listItemContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontFamily: 'Calibre-Regular,sans-serif',
    fontSize: 16,
    marginTop: '1px',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'end',
    gap: 4,
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  sliderBox: {
    paddingRight: '2rem',
  },
  caption: {
    marginTop: theme.spacing(1),
    fontFamily: 'Calibre-Regular',
    fontSize: 14,
  },
}));

const AudienceRow = ({
  data,
  selectedAudiences,
  setSelectedAudiences,
  modeledAudiencesCount,
}) => {
  const classes = useStyles();

  const marks = Array.from({ length: 11 }, (_, i) => ({
    value: i,
    label: `${i}%`,
  }));

  const currentSelectedAudience = selectedAudiences?.find(
    a => `${a?.id}` === `${data?.id}`
  );

  const lalModelPrecision = currentSelectedAudience?.lalModelPrecision;

  const handleSliderChange = (event, newValue) => {
    const updatedSelectedAudience = selectedAudiences.map(item =>
      `${item.id}` === `${data.id}` && item.type === data.audienceType
        ? { ...item, lalModelPrecision: newValue }
        : item
    );
    setSelectedAudiences(updatedSelectedAudience);
  };

  const RenderEstimatedAudienceSize = ({ groupedData }) => {
    const hasValues = Object.values(groupedData).some(arr => arr.length > 0);
    if (!hasValues && lalModelPrecision) {
      return null;
    }

    return (
      <div>
        {AudiencePlatformLabel.map(item => {
          const audienceCount = groupedData[item.TYPE]
            ? groupedData[item.TYPE][lalModelPrecision]?.audienceCount
            : null;
          const lowerBoundRange = audienceCount
            ? Math.round(audienceCount * 0.9)
            : 0;
          const upperBoundRange = audienceCount
            ? Math.round(audienceCount * 1.1)
            : 0;
          return (
            <div key={item.PRIMARY_TEXT} className={classes.listItemContainer}>
              <div className={classes.leftSection}>
                <b>{item.PRIMARY_TEXT}</b>
                <span className={classes.subText}>{item.SECONDARY_TEXT}</span>
              </div>
              <div className={classes.rightSection}>
                {numberFormatter(lowerBoundRange, 1)}
                {'  -  '}
                {numberFormatter(upperBoundRange, 1)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const audienceCountData = modeledAudiencesCount.find(
    a => `${a?.id}` === `${data?.id}` && a?.type === data?.audienceType
  )?.lalAudienceCount;

  const lalAudienceCount = groupModeledAudienceCountByIdType(audienceCountData);

  const audienceCountTotals = useMemo(() => {
    const totals = {};
    Object.values(lalAudienceCount).forEach(idTypeArray => {
      idTypeArray.forEach(({ percentileBreakpoint, audienceCount }) => {
        if (!totals[percentileBreakpoint]) {
          totals[percentileBreakpoint] = 0;
        }
        totals[percentileBreakpoint] += audienceCount;
      });
    });
    return totals;
  }, [lalAudienceCount]);

  const audienceCount = audienceCountTotals[lalModelPrecision];

  return (
    <>
      <Grid
        container
        alignItems='center'
        spacing={2}
        className={classes.segmentRow}
      >
        <Grid item xs={4}>
          <Typography variant='body1'>{data?.name}</Typography>
        </Grid>
        <Grid item xs={5}>
          <Box className={classes.sliderBox}>
            <Slider
              className={classes.slider}
              value={lalModelPrecision}
              onChange={handleSliderChange}
              step={1}
              valueLabelDisplay='auto'
              marks={marks}
              max={10}
            />
          </Box>
        </Grid>
        <Grid item xs={3} style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
          {audienceCount && (
            <>
              <span>
                <b>Estimated Size</b>: {numberFormatter(audienceCount, 1)}
              </span>
            </>
          )}
          <RenderEstimatedAudienceSize groupedData={lalAudienceCount} />
        </Grid>
      </Grid>
      <Divider />
    </>
  );
};

const GenerateAudience = ({
  selectedAudiences,
  setSelectedAudiences,
  operatorType,
  modeledAudiences,
  modeledAudiencesCount,
  isGenerating,
  setIsGenerating,
}) => {
  const classes = useStyles();
  const { data: user } = useUser();
  const currentUser = user || { email: '' };
  const dispatch = useAppDispatch();

  const currentAudience = useAppSelector(state => {
    return state.audienceReducer.currentAudience;
  });

  const lastGenerationCopletedAt = useMemo(() => {
    if (currentAudience?.lastGenerationCopletedAt) {
      const date = new Date(currentAudience?.lastGenerationCopletedAt); // Convert to a Date object
      // Format the date as desired
      return date.toLocaleString();
    }
    return 'Audience never generated';
  }, [currentAudience?.lastGenerationCopletedAt]);

  const generateYourAudience = async () => {
    if (selectedAudiences.length < 2) {
      dispatch(
        addNotification({
          state: LoadingStates.ERROR,
          message: 'Please select at least two audiences.',
        })
      );
      return;
    } else if (selectedAudiences.length > 10) {
      dispatch(
        addNotification({
          state: LoadingStates.ERROR,
          message: 'You can select up to 10 audiences only.',
        })
      );
      return;
    }
    const updatedAudience = {
      id: currentAudience?.id,
      name: currentAudience?.name,
      updated_by_email: currentUser?.email,
      audiences: selectedAudiences,
      operator: operatorType,
      combinedAudienceId: currentAudience?.combined_audience_id,
      showLoader: false,
      audience_type: ENUM_SYSTEM_USED.COMBINED_AUDIENCE,
    };

    dispatch(updateCombinedAudience(updatedAudience));

    let index = 0;
    const includedAudiences = selectedAudiences.map(item => {
      const singleAudience = {
        audience_id: `${item.id}`,
        audience_type: item.type,
        order: index,
        operator: operatorType === OPERATOR_TYPE.OR ? 0 : 1,
        lal_model_id: modeledAudiences.find(aud => `${aud.id}` === `${item.id}`)
          ?.lalModelId,
        lal_model_precision: item.lalModelPrecision,
        lal_model_type: AUDIENCE_LAL_MODAL_TYPE.ATTAIN,
      };
      index++;
      return singleAudience;
    });
    const payload = [
      {
        id: `${currentAudience.id}`,
        emails: currentUser.email,
        exportType: COMBINED_AUDIENCE_GENERATION,
        lalModelType: AUDIENCE_LAL_MODAL_TYPE.ATTAIN,
        includedAudiences: includedAudiences,
      },
    ];

    setIsGenerating(true);
    axios
      .post(
        `${AUDIENCE_STUDIO_API_ENDPOINT}/generate-combined-audience`,
        payload
      )
      .then(() => {
        dispatch(
          addNotification({
            state: 'done',
            message: 'Your audience is getting generated',
          })
        );
      })
      .catch(() => {
        setIsGenerating(false);
        dispatch(
          addNotification({
            state: 'error',
            message: 'Your audience failed to get generated',
          })
        );
      });
  };

  return (
    <div className={classes.containerBorder}>
      <Card variant='outlined'>
        <CardContent>
          <div className={classes.generateHeaderText}>
            Generate Your Audience
          </div>
          {/* Header row */}
          <Grid container spacing={2} className={classes.headerRow}>
            <Grid item xs={4}>
              <Typography variant='subtitle2'>Name</Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography variant='subtitle2'>Audience Slider</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant='subtitle2'>Size</Typography>
            </Grid>
          </Grid>
          <Divider />
          {selectedAudiences.map((audience, index) => {
            const data = modeledAudiences?.find(
              a =>
                `${a.id}` === `${audience.id}` &&
                a.audienceType === audience.type
            );
            return (
              <AudienceRow
                key={index}
                data={data}
                selectedAudiences={selectedAudiences}
                setSelectedAudiences={setSelectedAudiences}
                modeledAudiencesCount={modeledAudiencesCount}
              />
            );
          })}

          <Box textAlign='right'>
            <Button
              color='primary'
              disableElevation
              variant='contained'
              style={{ marginTop: 16 }}
              onClick={generateYourAudience}
              disabled={isGenerating}
            >
              Generate
              {isGenerating && (
                <CircularProgress
                  size={24}
                  color='inherit'
                  style={{ marginLeft: 8 }}
                />
              )}
            </Button>
            <div className={classes.caption}>
              Last Generated: {lastGenerationCopletedAt}
            </div>
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default GenerateAudience;
